import { Component, inject, ViewChild } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { GeneratedTemplate } from 'src/modules/enaio/shared/GeneratedTemplate';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { AdvancedForm } from '../../models/advanced-form.model';
import { DataSourceHandler } from '../../models/data-source-handler.model';
import { GenDocField } from '../../models/gen-doc-field.model';
import { GenDoc } from '../../models/gen-doc.model';
import { DataSourceItem } from '../../models/data-source-item.model';
import { DatabaseUserGroup } from 'src/modules/database/shared/database-user-group.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { SmFormComponent } from 'src/modules/sm-base/components/sm-form/sm-form.component';
import { GenDocDto } from '../../models/gen-doc.dto';
import { MenuItem } from 'primeng/api';
import { SaveDataHandler } from 'src/modules/app-template/models/save-data-handler.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { GenDocAttachment } from '../../models/gen-doc-attachment.model';
import { FileUploadService } from 'src/modules/sm-base/services/file-upload-service';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';

@Component({
  selector: 'app-edit-invoice',
  templateUrl: './edit-invoice.component.html',
  styleUrl: './edit-invoice.component.scss'
})
export class EditInvoiceComponent extends ComponentView {

    _Utils = Utils;

    app = inject(MainAppService);
    fileUpload = inject(FileUploadService);

    neededParams: ObjectOfSimpleDataTypes = {
        id: "number?",
        copyType: "string?"
    }

    id = 0;
    copyType: string = null;
    doc: GenDoc;
    formId = "invoice";
    form: AdvancedForm;
    f: FrontendFormDefinition;
    startPrinting = false;
    
    userGroups: DatabaseUserGroup[];
    dataSources: DataSourceHandler;
    generated: GeneratedTemplate;
    previewBlob: Blob = null;
    debouncedPreview: any;
    splitterHeight = "0px";
    templateMenuItems: MenuItem[] = [
        {
            label: "Laden ...",
            command: this.loadTemplate.bind(this)
        },
        {
            label: "Speichern ...",
            command: this.saveTemplate.bind(this)
        },
        {
            label: "Verwalten ...",
            command: this.manageTemplates.bind(this)
        }
    ];
    copyInvoiceMenuItems: MenuItem[] = [];
    attachmentsTable: TableData;
    selectedAttachment: GenDocAttachment;
    accountingCutOffDate: Date;
            
    
    @ViewChild('pdfViewer') pdfViewer: any;
    @ViewChild("formComponent") formComponent: SmFormComponent;
    @ViewChild('pdfViewerAttachment') pdfViewerAttachment: any;

    ngDoCheck(): void {
        this.splitterHeight = this.formComponent?.elementRef?.nativeElement?.clientHeight;
    }

    async initParams(): Promise<boolean> {
        this.debouncedPreview = Utils.debounce(async () => this.preview(), 3000);
        if (this.id == null) {
            this.doc = new GenDoc();
        }
        else if (!Utils.isNoe(this.copyType)) {
            this.doc = await RestEndpoint.main().query({ id: this.id, copyType: this.copyType }).run("api/ivg/docCopy").get(GenDoc);
            this.id = null;
        }
        else {
            this.doc = Utils.arrayGetSafe(await RestEndpoint.main().query({id: this.id}).run("api/ivg/doc").list(GenDocDto), 0)?.doc;    
        }

        if (this.doc == null) {
            this.app.loadDataError("Die Rechnung wurde nicht gefunden oder Sie besitzen nicht die notwendigen Berechtigungen, um sie anzuzeigen", false, this);
            return false;
        }
        
        this.app.updateNavigation(this.id == null ? "Neue Rechnung erstellen" : "Rechnung bearbeiten", [{label: "Rechnungs-Übersicht", routerLink: ["/invoice-generator", "home"]}, {label: this.id == null ? "Neue Rechnung erstellen" : "Rechnung bearbeiten", routerLink: ["/invoice-generator", "edit-invoice"]}]);

        this.userGroups = await this.app.loadDataHandler(async () => RestEndpoint.main().run("api/app/usergroup").list(DatabaseUserGroup), this);
    
        let adminSettings = await this.app.loadDataHandler(async () => RestEndpoint.main().run("api/app/genericObjects/main/adminGlobal").getObject(), this) ?? {};
        this.accountingCutOffDate = Utils.dateFromString(adminSettings.accountingCutOffDate, "DD.MM.YYYY");        

        this.form = await RestEndpoint.main().query({id: this.formId}).run("api/ivg/getForm").get(AdvancedForm);
        
        this.dataSources = new DataSourceHandler(this.form.dataSources);

        this.f = new FrontendFormDefinition([
            new FrontendFieldDefinition("status", "Status", FrontendFieldType.comboBox, {
                dropdownEditable: false,
                enabled: false,
                guiGroup: "status",
                listItems: FrontendFieldListItem.make(["In Bearbeitung", "Warte auf Freigabe", "Freigegeben", "Abgeschlossen"], s => [s, s])
            }),
            new FrontendFieldDefinition("visibleForGroups", "Sichtbar für diese Gruppen", FrontendFieldType.comboBoxMulti, { 
                guiGroup: "status",
                guiGroupOwnLabel: true,
                listItems: FrontendFieldListItem.make(this.userGroups, ug => [ug.id, ug.name], true),
                onValueChanged: async () => {
                    let visibleForGroups = this.f.getValue("visibleForGroups") as number[];
                    let locations = Utils.arrayExplode(visibleForGroups, g => Utils.split(this.userGroups.find(ug => ug.id == g)?.getAttributeValue("locations"), ",")).map(loc => Utils.toNumber(Utils.getSubstringTo(loc, ":")));
                    let billerField = this.f.getField("biller");
                    let d = this.dataSources.get(billerField.dataSourceId).data;
                    billerField.listItems = FrontendFieldListItem.make(this.dataSources.get(billerField.dataSourceId).data
                            .filter(item => locations.includes(Utils.toNumber(item.data.Location))), item => [item.key, item.label], true);
                    
                }
            }),
            ...this.form.form.fields
        ]);

        await this.dataSources.getOrFetch("impersonalAccounts");
        await this.dataSources.getOrFetch("costCentersAndObjects");
        for (let field of this.form.form.fields) {
            if (!Utils.isNoe(field.dataSourceId)) {
                let ds = await this.dataSources.getOrFetch(field.dataSourceId);
                //PRAGMATIC (zukünftig konfigurierbar)
                if (ds.def.id == "locations") {
                    let allowedLocations = Utils.arrayGetUnique(Utils.arrayExplode(this.app.auth.userDetails?.getUserGroups(), ug => Utils.split(ug.getAttributeValue("locations") ?? "", ",")));
                    ds.data = ds.data.filter(dsi => allowedLocations.includes(dsi.data.Location) || allowedLocations.includes(dsi.data.Location + ':' + dsi.data.Number));
                }
                field.listItems = FrontendFieldListItem.make(ds.data, item => [item.key, item.label], true);
                if (ds.def.backendOnly) {
                    field.onAutoComplete = async query => {
                        if (Utils.isNoe(query)) {
                            return [];
                        }
                        else {
                            let queried = await RestEndpoint.main().query({formId: "invoice", dataSourceId: field.dataSourceId, query}).run("api/ivg/queryDataSourceBackend").list(DataSourceItem);
                            //PRAGMATIC konfigurierbar machen
                            if (field.id == "recipient" || field.id == "serviceRecipient") {
                                this.fillDoc(this.doc);
                                let client = this.doc.getField("client")?.getFullObject()?.data?.Number;
                                queried = queried.filter(item => item.data.BP_Client == client);
                            }
                            return FrontendFieldListItem.make(queried, item => [item.key, item.label, { internalObject: item }], true);
                        }
                    };
                }
            }
            if (field.type == FrontendFieldType.table) {
                field.generateEmptyTableItem = () => ({});
            }
            
            //PRAGMATIC (zukünftig konfigurierbar)
            if (field.id == "documentType") {
                field.onValueChanged = async () => {
                    this.f.getField("cancellationDocumentNumber").visible = this.f.getValue("documentType") == "Storno-Gutschrift";
                    this.f.getField("deliveryPeriodTo").visible = this.f.getValue("documentType") == "Dauerrechnung";
                    this.f.getField("deliveryPeriodFrom").caption = this.f.getValue("documentType") == "Dauerrechnung" ? "Leistungsperiode von" : "Leistungsperiode";
                    this.valueChanged();
                }
            }
            else if (field.id == "recipient" || field.id == "serviceRecipient") {
                field.onValueChanged = async () => {
                    this.zufallUpdateServiceFields();
                    this.valueChanged();
                }
            }
            else if (field.id == "biller") {
                field.onValueChanged = async (byUser) => {
                    let clientField = this.f.getField("client");
                    let biller = this.dataSources.get(field.dataSourceId).findByKey(field.getValue());
                    clientField.listItems = biller != null ? FrontendFieldListItem.make(this.dataSources.get(clientField.dataSourceId).data.filter(item => item.data.Number == biller.data.Number),
                        item => [item.key, item.label], true) : [];
                    clientField.enabled = clientField.listItems.length > 1;

                    if (byUser) {
                        clientField.setValue(Utils.arrayGetSafe(clientField.listItems, 0));
                        this.zufallUpdateServiceFields();
                    }
                    this.zufallUpdateDocumentNumber();

                    this.valueChanged();
                }
            }
            else if (field.id == "client" || field.id == "documentDate") {
                field.onValueChanged = () => {
                    this.zufallUpdateDocumentNumber();
                    this.valueChanged();
                }
            }
            else if (field.id == "deliveryPeriodFrom") {
                field.validator = (field, _) => {
                    if (!Utils.isDate(field.getValue())) {
                        return null;
                    }
                    if (field.getValue() > new Date()) {
                        return "Die Leistungsperiode darf nicht in der Zukunft liegen";
                    }
                    if (this.accountingCutOffDate != null && field.getValue() < this.accountingCutOffDate) {
                        return "Die Leistungsperiode liegt weiter in der Vergangenheit als der Buchungsschluss (" + Utils.dateFormatDefaultDate(this.accountingCutOffDate) + ")";
                    }
                    let isDauer = this.f.getValue("documentType") == "Dauerrechnung";
                    if (Utils.isDate(this.f.getValue("documentDate")) && Utils.dateDiffDays(field.getValue() as Date, this.f.getValue("documentDate") as Date) > 365) {
                        return "Die Leistungsperiode darf maximal 12 Monate vor dem Rechnungsdatum liegen";
                    }
                    return null;
                };
            }
            else if (field.id == "positions") {
                field.generateEmptyTableItem = () => ({
                    isText: false
                });

                for (let col of field.tableColumns) {
                    col.getStyleClassCallback = value => value.row.raw.isText ? "xbg-gray-200" : "";
                }

                field.validator = (field, _) => {
                    let value: OrdinaryObject[] = field.getValue();
                    let i = 0;
                    for (let row of value) {
                        i++;
                        if (!row.isText && (row.amount == null || row.amount == 0)) {
                            return "Belegpositionen Zeile " + i + ": Das Feld 'Anzahl' muss gefüllt werden"
                        }
                        if (!row.isText && (row.singlePriceNet == null || row.singlePriceNet == 0)) {
                            return "Belegpositionen Zeile " + i + ": Das Feld 'Einzelpreis netto' muss gefüllt werden"
                        }
                    }
                    return null;
                };

                field.onValueChanged = () => {
                    let value: OrdinaryObject[] = field.getValue();
                    let posNumber = 1;
                    let sum = 0;
                    for (let row of value) {
                        row.positionPriceNet = Utils.isNumber(row.singlePriceNet) && Utils.isNumber(row.amount) ? row.singlePriceNet * row.amount : "";
                        if (row.isText) {
                            row.posNumber = "";
                            row.type = "";
                            row.costCenterSystem = "";
                            row.dimension = "";
                            row.amount = 0;
                            row.singlePriceNet = 0;
                            row.positionPriceNet = 0;
                        }
                        else {
                            row.posNumber = posNumber++;
                        }
                        sum += Utils.toNumber(row.positionPriceNet, true, true, 0);
                        field.tableMustRefresh = true;
                    }
                    this.f.getField("totalPriceNet").setValue(sum);
                };
                field.tableColumns.find(tc => tc.id == "costCenterSystem").editor = (e: TableCellEvent) => {
                    let clientField = this.f.getField("client");
                    let biller = this.dataSources.get(this.f.getField("biller").dataSourceId).findByKey(this.f.getField("biller").getValue());
                    clientField.listItems = biller != null ? FrontendFieldListItem.make(this.dataSources.get(clientField.dataSourceId).data.filter(item => item.data.Number == biller.data.Number),
                        item => [item.key, item.label], true) : [];
                    clientField.enabled = clientField.listItems.length > 1;

                    let billerLocationFormatted = biller != null ? (biller.data.Location.length == 1 ? "0" : "") + biller.data.Location : "";
                                        
                    return new FrontendFieldDefinition("editor", "", FrontendFieldType.comboBox, { 
                        dropdownEditable: false,
                        listItems: biller != null ? 
                        FrontendFieldListItem.make(this.dataSources.get("costCentersAndObjects").data
                            .filter(item => item.data.Mandant == biller.data.Number && (item.data.CCNummer.startsWith("T" + billerLocationFormatted) || item.data.CCNummer.startsWith("R" + billerLocationFormatted))),
                            item => [item.key, item.label], true) : []
                    });
                }
                field.tableColumns.find(tc => tc.id == "costCenterSystem").valueFormatter = v => {
                    let parts = Utils.split(Utils.toString(v), "|||");
                    return parts.length == 3 ? parts[1] + " - " + parts[2] : "";
                }
                field.tableColumns.find(tc => tc.id == "type").editor = (e: TableCellEvent) => {
                    let biller = this.dataSources.get(this.form.form.getField("biller").dataSourceId).findByKey(this.f.getField("biller").getValue());
                    let recipient = this.f.getField("serviceRecipient").value?.internalObject ?? this.f.getField("recipient").value?.internalObject ?? null;
                    let documentType = this.f.getValue("documentType");
                    return new FrontendFieldDefinition("editor", "", FrontendFieldType.comboBox, { 
                        dropdownEditable: false,
                     //   dropdownAutoComplete: true,
                        listItems: biller != null ? FrontendFieldListItem.make(this.dataSources.get("impersonalAccounts").data.filter(item => this.zufallImpersonalAccountMatches(item, biller, recipient, documentType)),
                            item => [item.key, item.label], true) : []
                    });
                }
                field.tableColumns.find(tc => tc.id == "type").valueFormatter = v => {
                    let parts = Utils.split(Utils.toString(v), "|||");
                    return parts.length == 2 ? parts[0] : "";
                }
                field.tableColumns.find(tc => tc.id == "singlePriceNet").valueFormatter = v => Utils.isNoe(v) ? "" : Utils.formatNumber(Utils.toNumber(v), 8, true, true, false)
                field.tableColumns.find(tc => tc.id == "positionPriceNet").valueFormatter = v => Utils.isNoe(v) ? "" : Utils.formatNumber(Utils.toNumber(v), 2, true, true, false)

                for (let col of field.tableColumns) {
                    if (col.id != "posNumber" && col.id != "isText" && col.id != "typeText") {
                        col.editableCallback = e => !e.row.raw.isText;
                    }
                }
                
            }
            
            field.onValueChanged ??= async (): Promise<any> => {
                this.valueChanged();
            };
        
        }

        if (this.id == null && Utils.isNoe(this.copyType)) {
            let dbUser = this.app.auth.userDetails.getDatabaseUser();
            this.doc.status = "In Bearbeitung";
            this.doc.visibleForGroups = [...dbUser.groups];
            this.doc.fields.push(Utils.fromPlain(GenDocField, { 
                fieldId: "billerContactPerson",
                value: "\"" + dbUser.getFullName() + "\""
            }));
            this.doc.fields.push(Utils.fromPlain(GenDocField, { 
                fieldId: "billerContactPersonPhone",
                value: "\"" + Utils.stringDef(dbUser.phone) + "\""
            }));     
            this.doc.fields.push(Utils.fromPlain(GenDocField, { 
                fieldId: "billerContactPersonMail",
                value: "\"" + Utils.stringDef(dbUser.mail) + "\""
            }));     
            this.doc.fields.push(Utils.fromPlain(GenDocField, { 
                fieldId: "positions",
                value: "[]"
            }));     
            this.doc.fields.push(Utils.fromPlain(GenDocField, { 
                fieldId: "currency",
                value: "\"EUR\""
            }));        
        }
        let docDate = this.doc.getField("documentDate") ?? Utils.arrayAddThrough(this.doc.fields, Utils.fromPlain(GenDocField, { 
            fieldId: "documentDate"
        }));
        docDate.value = Utils.toJson(new Date());

        this.fillGuiWithDoc(this.doc);
        
        this.copyInvoiceMenuItems = Utils.arrayWithoutNull([
            this.doc.status == "Abgeschlossen" ? {
                label: "Storno-Gutschrift zu dieser Rechnung erstellen",
                command: () => this.newCopiedInvoice(this.doc.id, "creditNote")
            } : null,
            {
                label: "Diese Rechnung kopieren",
                command: () => this.newCopiedInvoice(this.doc.id, "copy")
            }
        ]);

        this.updateAttachmentsTable();

        //this.preview();
        
        return true;
    }

    async newCopiedInvoice(id: number, copyType: string): Promise<void> {
        if (!await this.app.messageDialog.yesNo("Wenn Sie fortfahren, werden eventuelle Änderungen an dieser Rechnung nicht gespeichert und die kopierte Rechnung wird geöffnet. Fortfahren?", "Warnung")) {
            return;
        }

        await this.app.navigateTo(["/invoice-generator", "edit-invoice", id, copyType]);
    }


    zufallImpersonalAccountMatches(item: DataSourceItem, biller: DataSourceItem, recipient: DataSourceItem, documentType: string): boolean {
        if (biller?.data == null || recipient?.data == null || Utils.isNoe(documentType) || item.data.Mandant != biller.data.Number || biller.data.Country != "DE") {
            return false;
        } 
        let number = Utils.toString(item.data.Nummer);
        if (recipient.data.BP_Country == "DE") {
            switch (documentType) {
                case "Verkaufsrechnung":
                case "Dauerrechnung":
                case "Schaden-Ausgangsrechnung":
                case "Storno-Schadenausgangsrechnung":
                    return number.startsWith("S4") && number.endsWith("01");
                case "Gutschrift":
                case "Storno-Gutschrift":
                    return number.startsWith("S5") && number.endsWith("01") || number.startsWith("S6") || number.startsWith("S76");
                case "Schaden-Gutschrift":
                case "Storno-Schadengutschrift":
                    return number.startsWith("S5") && number.endsWith("01") || number.startsWith("S6");
                default:
                    return false;
            }
        }
        else if (["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SW"].includes(recipient.data.BP_Country)) {
            switch (documentType) {
                case "Verkaufsrechnung":
                case "Dauerrechnung":
                case "Schaden-Ausgangsrechnung":
                case "Storno-Schadenausgangsrechnung":
                    return number.startsWith("S4") && number.endsWith("16");
                case "Gutschrift":
                case "Storno-Gutschrift":
                case "Schaden-Gutschrift":
                case "Storno-Schadengutschrift":
                    return number.startsWith("S5") && number.endsWith("16");
                default:
                    return false;
            }            
        }
        else {
            switch (documentType) {
                case "Verkaufsrechnung":
                case "Dauerrechnung":
                case "Schaden-Ausgangsrechnung":
                case "Storno-Schadenausgangsrechnung":
                    return number.startsWith("S4") && number.endsWith("03");
                case "Gutschrift":
                case "Storno-Gutschrift":
                case "Schaden-Gutschrift":
                case "Storno-Schadengutschrift":
                    return number.startsWith("S5") && number.endsWith("03");
                default:
                    return false;
            }
        }
    }

    zufallUpdateServiceFields(): void {
        let biller = this.dataSources.get(this.f.getField("biller").dataSourceId).findByKey(this.f.getValue("biller"));
        let recipient = this.f.getField("serviceRecipient").value?.internalObject ?? this.f.getField("recipient").value?.internalObject ?? null;
        
        this.f.setValue("locationOfServiceProvider", biller != null ? biller.data.Country : "");                    
        this.f.setValue("locationOfServiceReceiver", recipient?.data?.BP_Country ?? "");                    
        this.f.setValue("servicePath", recipient == null || biller == null ? "" : 
            biller.data.Country == "DE" ? recipient.data.BP_Country == "DE" ? "Inland zu Inland" : "Inland zu Ausland" :
            recipient.data.BP_Country == "DE" ? "Ausland zu Inland" : "Ausland zu Ausland");                    
    }

    zufallUpdateDocumentNumber(): void {
        this.fillDoc(this.doc);

        let client = this.doc.getField("client")?.getFullObject()?.data?.Number;
        let biller = this.doc.getField("biller")?.getFullObject()?.data?.Location;
        let date = this.doc.getField("documentDate").getValue(this.f.getField("documentDate").type);
        this.f.setValue("documentNumber", (client != null ? client.padStart(3, '0') : "???") + (biller != null ? biller.padStart(2, '0') : "??") + (date != null ? date.getFullYear() % 100 : "????") + "???????? (vorläufige Nummer)");
    }

    async save(): Promise<void> {
        this.app.saveDataHandler(async () => {
            let id = await RestEndpoint.main().post().body(this.fillDoc(this.doc)).run("api/ivg/doc").getNumber();
            if ((this.id == null || this.id <= 0) && id > 0) {
                this.id = id;
                this.doc.id = id;
            }
        }, this, true, false);
    }

    async finish(): Promise<void> {
        var errors = this.f.validate();
        if (errors != null) {
            this.app.messageDialog.infoNoWait("Die Rechnung kann nicht fertiggestellt werden. Es wurden noch Fehler gefunden:<br><br>" + Utils.arrayItemsToString(Utils.objectGetValues(errors), "<br>"));
            return;    
        }
        if (!await this.app.messageDialog.yesNo("Wenn Sie die Rechnung fertigstellen, ist Sie danach nicht mehr editierbar. Möchten Sie fortfahren?", "Warnung")) {
            return;
        }
        this.app.saveDataHandler2(new SaveDataHandler({
            worker: async () => {
                this.fillDoc(this.doc);
                this.doc.status = "Abgeschlossen";
                let id = await RestEndpoint.main().post().body(this.doc).run("api/ivg/doc").getNumber();
                if ((this.id == null || this.id <= 0) && id > 0) {
                    this.id = id;
                    this.doc.id = id;
                }
            },
            successToast: true, 
            navigateTo: ["/invoice-generator", "home"], 
            successTitle: "Erfolgreich", 
            successText: "Die Rechnung wurde erfolgreich fertiggestellt"
        }));
    }

    async cancel(): Promise<void> {
        if (this.doc.status == 'Abgeschlossen' || await this.app.messageDialog.yesNo("Sind Sie sicher, dass Sie alle Änderungen verwerfen möchten?", "Warnung")) {
            this.app.navigateBack();
        }
    }

    async displayDocData(): Promise<void> {
        let data = await RestEndpoint.main().post().body(this.fillDoc(this.doc)).run("api/ivg/getDictForGeneration").getOrdinaryObject(Object);
        this.app.messageDialog.textArea("RAW-Daten des Dokuments", Utils.toJson(data, true));            
    }
    
    async toService(): Promise<void> {
        var errors = this.f.validate();
        if (errors != null) {
            this.app.messageDialog.infoNoWait("Die Übergabe kann nicht gestartet werden. Es wurden noch Fehler gefunden:<br><br>" + Utils.arrayItemsToString(Utils.objectGetValues(errors), "<br>"));
            return;    
        }
        await RestEndpoint.main().post().body(this.fillDoc(this.doc)).run("api/ivg/simulateFinish").getText();
    }

    valueChanged(): void {
        //this.debouncedPreview();
        this.f.validate();
    }

    fillGuiWithDoc(doc: GenDoc): void {
        this.f.setValue("status", doc.status);
        this.f.setValue("visibleForGroups", doc.visibleForGroups.map(g => g.id));
        
        for (let field of this.form.form.fields) {
            let gdf = doc.getField(field.id);
            if (gdf != null) {
                //TODO Warum!?
                field.value = gdf.getValue(field.type);
                if (field.dropdownAutoComplete) {
                    if (!Utils.isNoe(field.listItems)) {
                        field.value = field.listItems.find(li => li.value == field.value) ?? field.value;
                    }
                    else if (gdf.fullObject != "null") {
                        let dsi = Utils.fromPlain(DataSourceItem, gdf.getFullObject());
                        field.value = new FrontendFieldListItem(dsi.key, dsi.label, { internalObject: dsi });
                    }
                }
            }
            else {
                field.value = FrontendFieldType.getDefaultValue(field.type);
            }
        }

        if (this.doc.status == "Abgeschlossen") {
            for (let field of this.f.fields) {
                field.enabled = false;
            }
        }
        else {
            //PRAGMATIC
            this.f.getField("visibleForGroups").onValueChanged(false);
            this.f.getField("documentType").onValueChanged(false);
            this.f.getField("biller").onValueChanged(false);

            this.f.validate();
            //AngularTimer funktioniert hier nicht. Es benötigt anscheinend die Zeit, um die Form aufzubauen
            Utils.setTimerOnce(200, () => {
                let firstEnabled = this.f.fields.find(f => f.enabled);
                if (firstEnabled != null) {
                    firstEnabled.requestFocus = true;
                }
            });
        }
        
    }

    fillDoc(doc: GenDoc): GenDoc {
        doc.type = this.formId;
        doc.status = this.f.getValue("status");
        doc.visibleForGroups = this.f.getValue("visibleForGroups").map(id => Utils.fromPlain(DatabaseUserGroup, { id }));
        for (let field of this.form.form.fields) {
            let gdf = doc.getField(field.id);
            if (gdf == null) {
                gdf = new GenDocField();
                this.doc.fields.push(gdf);
                gdf.fieldId = field.id;
            }

            //Sonderbehandlung für Items die nicht im DataSource vorhanden sind, sondern dynamisch über Suggestions geladen wurden
            if (field.value instanceof FrontendFieldListItem && field.value.internalObject != null) {
                gdf.value = Utils.toJson(field.getValue(false));
                gdf.fullObject = Utils.toJson(field.value.internalObject);
            }
            else {
                let plainValue = field.getValue(false);
                gdf.value = Utils.toJson(plainValue);
                if (!Utils.isNoe(field.dataSourceId)) {
                    let ds = this.dataSources.get(field.dataSourceId);
                    gdf.fullObject = Utils.toJson(ds.findByKey(plainValue));
                }
                else {
                    gdf.fullObject = "null";
                }
            }            
        }    
        let result = Utils.cloneDeep(doc);
        return result;
    }

    async preview(): Promise<void> {
        this.generated = await RestEndpoint.main().post().body(this.fillDoc(this.doc)).run("api/ivg/generateDocument").get(GeneratedTemplate);
        this.previewBlob = new Blob([GuiUtils.base64ToArrayBuffer(this.generated.fileBase64)], { type: 'application/pdf' });
        if (this.pdfViewer != null) {
            this.pdfViewer.pdfSrc = this.previewBlob;
            this.pdfViewer.refresh();
        }
    }

    async print(): Promise<void> {
        await this.preview();
        this.startPrinting = true;
      }
    
      pdfLoaded(): void {
        if (this.startPrinting) {
            this.startPrinting = false;
            document.getElementsByTagName("iframe")[0].contentWindow.print();
        }
      }

    async loadTemplate(): Promise<void> {
        let templates = await RestEndpoint.main().query({templates: true}).run("api/ivg/doc").list(GenDocDto); 
        if (templates.length == 0) {
            this.app.showToast("error", "Keine Vorlagen", "Es sind keine gespeicherten Vorlagen vorhanden");
            return;
        }   
        let form = new FrontendFormDefinition([
            new FrontendFieldDefinition("template", "Vorlage", FrontendFieldType.comboBox, { 
                mandatory: true, 
                dropdownEditable: false,
                value: "", 
                listItems: [new FrontendFieldListItem("", ""), ...FrontendFieldListItem.make(templates, t => [t.doc.id, t.doc.templateName])] //TODO Bug mit schmaler Dropdownbox - vielleicht behoben mti PrimeNG >= 19
            })
        ]);
        if ((await this.app.messageDialog.form(form, "Welche Vorlage möchten Sie laden?")) != "ok") {
            return;
        }
        let id = this.doc.id;
        this.doc = Utils.cloneDeep(templates.find(t => t.doc.id == form.getValue("template") as number).doc);
        for (let field of this.doc.fields) {
            field.id = 0;
        }
        this.doc.templateName = null;
        this.doc.id = id;
        this.fillGuiWithDoc(this.doc);
    }

    async manageTemplates(): Promise<void> {
        let templates = await RestEndpoint.main().query({templates: true}).run("api/ivg/doc").list(GenDocDto); 
        if (templates.length == 0) {
            this.app.showToast("error", "Keine Vorlagen", "Es sind keine gespeicherten Vorlagen vorhanden");
            return;
        }   
        let form = new FrontendFormDefinition([
            new FrontendFieldDefinition("template", "Vorlage", FrontendFieldType.comboBox, { 
                mandatory: true, 
                dropdownEditable: false,
                value: "", 
                listItems: [new FrontendFieldListItem("", ""), ...FrontendFieldListItem.make(templates, t => [t.doc.id, t.doc.templateName])] //TODO Bug mit schmaler Dropdownbox - vielleicht behoben mti PrimeNG >= 19
            })
        ]);
        let clicked = await this.app.messageDialog.form(form, "Welche Vorlage möchten Sie laden?", [new ButtonDefinition("delete", "Löschen", true, ""), new ButtonDefinition("rename", "Umbenennen", true, ""), this.app.messageDialog.cancel]);
        if (clicked == "delete") {
            await RestEndpoint.main().query({id: form.getValue("template")}).delete().run("api/ivg/template").getText(); 
        }
        else if (clicked == "rename") {
            let newName = await this.app.messageDialog.input<string>(new FrontendFieldDefinition("", "Neuer Name", FrontendFieldType.text, { mandatory: true }), "Umbenennen");
            if (newName != null) {
                await RestEndpoint.main().query({id: form.getValue("template"), name: newName}).post().run("api/ivg/templateRename").getText(); 
            }
        
        }
    }
    
    async saveTemplate(): Promise<void> {
        let templates = await RestEndpoint.main().query({templates: true}).run("api/ivg/doc").list(GenDoc); 
        let form = new FrontendFormDefinition([
            new FrontendFieldDefinition("templateName", "Name der Vorlage", FrontendFieldType.text, { mandatory: true})
        ]);
        if (await this.app.messageDialog.form(form, "Unter welchem Namen soll die Vorlage gespeichert werden?") != "ok") {
            return;
        }
        let templateName = form.getValue("templateName") as string;
        
        let existing = templates.find(t => t.templateName == templateName);
        if (existing != null && !(await this.app.messageDialog.yesNo("Eine Vorlage mit diesem Namen existiert bereits. Möchten Sie sie überschreiben?", "Warnung", "no"))) {
            return;
        }

        let doc = this.fillDoc(existing ?? this.doc);
        doc.templateName = templateName;
        doc.id = existing?.id ?? 0;
        await RestEndpoint.main().post().body(doc).run("api/ivg/doc").getString();
    }

    updateAttachmentsTable(): void {
        this.attachmentsTable = new TableData([
            new TableColumn("name", "Name", TableCellType.text, { flex: 1 })
        ], this.doc.attachments.map(attachment => new TableRow(Utils.toString(attachment.id), attachment, {
            name: attachment.name
        })), [new TableSortColumn("name")]);
    }

    async attachDocument(): Promise<void> {
        let uploadedFile = (await this.fileUpload.chooseFile())[0];
        let attachment = Utils.fromPlain(GenDocAttachment, {
            name: uploadedFile.legacy.name,
            sort: 0,
            data: Utils.bytesToBase64(uploadedFile.contents),
            genDocId: this.doc.id
        });
        attachment.id = await RestEndpoint.main().body(attachment).post().run("api/ivg/attachment").getNumber();

        this.doc.attachments.push(attachment);
        this.updateAttachmentsTable();
    }

    _removeAttachment = this.removeAttachment.bind(this);
    
    async removeAttachment(row: TableRow, rowIndex: number): Promise<void> {
        if (!await this.app.messageDialog.yesNo("Sind Sie sicher, dass dieser Anhang gelöscht werden soll?", "Warnung")) {
            return;
        }
        let id = (row.raw as GenDocAttachment).id;
        await RestEndpoint.main().query({id}).delete().run("api/ivg/attachment").getText();
        this.doc.attachments = this.doc.attachments.filter(a => a.id != id);
        this.updateAttachmentsTable();
    }

    selectAttachment(event: any): void {
        this.selectedAttachment = event.raw as GenDocAttachment;
        if (this.pdfViewerAttachment != null) {
            if (this.selectedAttachment == null) {
                this.pdfViewerAttachment.pdfSrc = "";
                this.pdfViewerAttachment.refresh();
            }
            else {
                RestEndpoint.main().query({id: this.selectedAttachment.id}).run("api/ivg/attachment").get(GenDocAttachment).then(b => {
                    this.pdfViewerAttachment.pdfSrc = GuiUtils.base64ToBlob(b.data);
                    this.pdfViewerAttachment.refresh();
                }).catch(_ => {
                    this.pdfViewerAttachment.pdfSrc = "";
                    this.pdfViewerAttachment.refresh();
                });
            }
        }
    }
    
}
